import { describeError, useApi } from '@classgo/app-kit';
import type { GuardianListItem } from '@classgo/shared';
import { Button, Card, EmptyState, Input, Modal, Table } from '@classgo/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { GuardianForm } from '../../forms/guardian-form';
import { useT } from '../../i18n';
import { peopleQueries } from '../../people/api';
import { useTenant } from '../../tenant/tenant-provider';
import { InvitationStatus } from '../student-detail';

/**
 * Guardians (acudientes) of the school: search by name, email or document, create and edit,
 * and invite from the row.
 */
export const GuardiansScreen = () => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const { session } = useTenant();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<GuardianListItem | null>(null);
  const guardians = useQuery(
    peopleQueries.guardians(api, { ...(search.trim() ? { search: search.trim() } : {}), page }),
  );
  const can = (key: string) => session.permissions.all || session.permissions.keys.includes(key);
  const refresh = () => queries.invalidateQueries({ queryKey: ['people', 'guardians'] });
  const total = guardians.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / 50));
  return (
    <>
      <Card
        title={t('guardians.title')}
        flush
        actions={
          can('guardians:write') ? (
            <Button onClick={() => setCreating(true)}>{t('guardians.new')}</Button>
          ) : undefined
        }
      >
        <div className="px-4 pt-4">
          <Input
            label={t('students.filter.search')}
            help={t('guardians.search.help')}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </div>
        {guardians.isError ? (
          <div className="p-6">
            <EmptyState
              title={t('entity.error.title')}
              description={describeError(t, guardians.error)}
            />
          </div>
        ) : (
          <Table<GuardianListItem>
            caption={t('guardians.caption')}
            rows={guardians.data?.items ?? []}
            rowKey={(row) => row.id}
            loading={guardians.isPending}
            {...(can('guardians:write')
              ? { onRowClick: (row: GuardianListItem) => setEditing(row) }
              : {})}
            empty={
              <EmptyState
                title={t('guardians.empty.title')}
                description={t('guardians.empty.body')}
              />
            }
            columns={[
              {
                key: 'name',
                header: t('guardians.column.name'),
                cell: (row) => (
                  <div>
                    <p className="font-medium text-gray-900">
                      {row.familyName}, {row.givenName}
                    </p>
                    <p className="text-xs text-gray-500">{row.email ?? row.phone ?? ''}</p>
                  </div>
                ),
              },
              {
                key: 'document',
                header: t('students.column.document'),
                cell: (row) =>
                  row.documentNumber
                    ? `${row.documentType ?? ''} ${row.documentNumber}`
                    : t('common.none'),
              },
              {
                key: 'students',
                header: t('guardians.column.students'),
                align: 'right',
                cell: (row) => row.studentsCount,
              },
              {
                key: 'invitation',
                header: t('guardians.column.invitation'),
                cell: (row) =>
                  can('users:invite') ? (
                    <span onClick={(event) => event.stopPropagation()}>
                      <InvitationStatus userId={row.id} email={row.email} />
                    </span>
                  ) : (
                    t('common.none')
                  ),
              },
            ]}
          />
        )}
        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm text-gray-500">
          <span>{t('guardians.total', { total })}</span>
          <div className="flex items-center gap-2">
            <span>{t('students.page', { page, pages })}</span>
            <Button
              size="sm"
              variant="ghost"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              {t('students.prev')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={page >= pages}
              onClick={() => setPage(page + 1)}
            >
              {t('students.next')}
            </Button>
          </div>
        </div>
      </Card>
      <Modal open={creating} onClose={() => setCreating(false)} title={t('guardians.new')}>
        {creating ? (
          <GuardianForm
            current={null}
            onDone={() => {
              setCreating(false);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
      <Modal open={editing !== null} onClose={() => setEditing(null)} title={t('guardians.edit')}>
        {editing ? (
          <GuardianForm
            current={editing}
            onDone={() => {
              setEditing(null);
              void refresh();
            }}
          />
        ) : null}
      </Modal>
    </>
  );
};
